import type { Column } from '@shared/types/data-table'
import type { Tariff } from '@features/billing-admin/types/tariff.types'
import type { ColumnManagementConfig } from '@shared/lib/tables/utils/column-management'
import { AppStatusBadge } from '@shared/components/ui/status-badge/AppStatusBadge'
import { PROGRAM_OPTIONS } from '@shared/constants/program-constants'
import {
  createIdColumn,
  createTextColumn,
  createDateColumn,
  createCustomColumn,
} from './column-factories'

export const TARIFF_COLUMNS: Record<string, Column<Tariff>> = {
  id: createIdColumn<Tariff>(),
  name: createTextColumn<Tariff>('name', 'Name', 180),
  status: createCustomColumn<Tariff>(
    'status',
    '',
    120,
    (tariff: Tariff) => (
      <AppStatusBadge entityType="tariff" status={tariff.status || 'unknown'} />
    )
  ),
  description: createTextColumn<Tariff>(
    'description',
    'Description',
    220,
    'text-sm text-muted-foreground'
  ),
  bank: createTextColumn<Tariff>(
    'bank',
    'Bank',
    140,
    'text-sm text-muted-foreground'
  ),
  program: createCustomColumn<Tariff>(
    'program',
    'Program',
    140,
    (tariff: Tariff) => {
      const label = PROGRAM_OPTIONS.find((p) => p.value === tariff.program)?.label || tariff.program
      return <span className="text-sm text-muted-foreground">{label}</span>
    }
  ),
  merchant: createTextColumn<Tariff>(
    'merchant',
    'Merchant',
    180,
    'text-sm text-muted-foreground'
  ),
  createdAt: createDateColumn<Tariff>('createdAt', 'Created', 140, false),
  effectiveDate: createDateColumn<Tariff>('effectiveDate', 'Effective', 140, false),
  updatedAt: createDateColumn<Tariff>('updatedAt', 'Updated', 140, false),
}

export const TARIFF_STATUS_CONFIG = {
  all: ['id', 'name', 'status', 'description', 'bank', 'program', 'merchant', 'createdAt', 'effectiveDate', 'updatedAt'],
  active: ['id', 'name', 'status', 'description', 'bank', 'program', 'merchant', 'createdAt', 'effectiveDate', 'updatedAt'],
  inactive: ['id', 'name', 'status', 'description', 'bank', 'program', 'merchant', 'createdAt', 'updatedAt'],
  draft: ['id', 'name', 'status', 'description', 'bank', 'program', 'merchant', 'createdAt', 'effectiveDate', 'updatedAt'],
  archived: ['id', 'name', 'status', 'description', 'bank', 'program', 'merchant', 'createdAt', 'updatedAt'],
} as const

export const getTariffColumns = (status: string = 'all'): Column<Tariff>[] => {
  const keys = TARIFF_STATUS_CONFIG[status as keyof typeof TARIFF_STATUS_CONFIG] || TARIFF_STATUS_CONFIG.all
  return keys.map(key => TARIFF_COLUMNS[key]).filter(Boolean)
}

export const getTariffColumnConfig = (status: string = 'all'): ColumnManagementConfig[] => {
  const visibleKeys: Set<string> = new Set(TARIFF_STATUS_CONFIG[status as keyof typeof TARIFF_STATUS_CONFIG] || TARIFF_STATUS_CONFIG.all)
  return Object.entries(TARIFF_COLUMNS).map(([key, col], index) => ({
    id: col.key,
    label: typeof col.header === 'string' ? col.header : col.key,
    visible: visibleKeys.has(key),
    required: ['id', 'name', 'status'].includes(key),
    order: index
  }))
}
